/* eslint-disable max-lines */
/* eslint-disable dot-notation */

import { action, computed, intercept, observable, runInAction } from 'mobx';

import { AnalyticsStore, ContextStore, ExperimentStore, PageStore, SerializedData, Store, timeoutPromise } from 'bernie-core';
import { getBexApiContext } from 'bernie-context';
import { getErrorString, Logger, NOOP_LOGGER } from 'bernie-logger';
import { ILocalization, Localization } from 'bernie-l10n';

import { BexApiSource } from 'sources/bex-api-source';
import { HotelSearchApiSource } from 'sources/hotel-search-api-source';
import { RateCalendarApiSource } from 'sources/rate-calendar-api-source';

import { adaptPropertyInfoDetails } from 'stores/adapters/bex-api/migration-adapters/property-info/property-info-adapter';
import { adaptPropertyInfoOffers } from 'stores/adapters/bex-api/migration-adapters/property-info/property-info-offers-adapter';
import { getDefaultTimeout, getOffersTimeout } from 'stores/multi-item-store-util';
import { getPropertiesFromResults, getPropertySearchInput, getShoppingContextInput } from 'stores/adapters';
import { InfositeQueryAdapter } from 'stores/adapters/infosite-query-adapter';
import { MultiItemStore } from 'stores/multi-item-store';
import { PropertySort, SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';
import { STORE_EVENTS } from 'stores/events/store-events';

import {
  DefaultRequestFields,
  HotelDetailsViewModel,
  HotelMapModel,
  HotelRoomsAndRatesViewModel,
  HotelViewModel,
  PropertyLocationScoreInfoModel,
  PropertyRoomScoreInfoModel,
  RateCalendarModel,
} from 'models';
import { BreadcrumbsViewModel } from 'models/common/breadcrumbs-model';
import { ExtendedContextStore, Geo, ViewModelResponse } from 'types/flex-definitions';
import { MultiItemContext } from 'models/multi-item/multi-item-types';

import { DateUtils } from 'utils/date-utils';
import { evaluateAndLogExperiment } from './utils';
import { mergeProductStrings } from 'utils/infosite-utils';

import {
  ContextInput,
  LodgingPwaPropertySearch,
  OperationResult,
  Points,
  PropertyAvailabilityCheck,
  PropertyFields,
  PropertyInfoDetails,
  PropertyInfoOffersBexFeatures,
  PropertyLocationScoreInfo,
  PropertyRoomScoreInfo,
  PropertySearchTravelAds,
  ShareInfo,
  UnitFragment,
} from '__generated__/api';
import { Base64 } from 'js-base64';
import { getPropertyAvailabilityCheck, getPropertyInfoInput, getPropertyOffersInput } from './adapters/bex-api/property-info';
import { MessagingCardProps } from 'components/common/messaging-card/messaging-card';
import { parse, stringify } from 'query-string';

import ShareInfoModel = ShareInfo.ShareInfo;

interface CurrentHotelStoreDefaults {
  hotelId?: number;
  selectedRoomType: string;
  selectedRatePlan: string;
  multiItemPriceToken?: string | undefined;
  srpDisplayPrice?: number | undefined;
  srpCurrency?: string | undefined;
  l10n: ILocalization;
  offerSearchData: Record<string, unknown>;
  reviewIdsUpvoted: string[];
  poiData: Record<string, unknown>;
  pageUsableMarked: boolean;
  propertyRoomScoreInfo: PropertyRoomScoreInfoModel[];
  propertyLocationScoreInfo: PropertyLocationScoreInfoModel;
  selectedGalleryCategoryId: string;
  inRoomInfoDialog?: boolean;
  shareInfo?: ShareInfoModel;
  referrer?: string | undefined;
  offerTimestamp?: string | undefined;
}

export class CurrentHotelStore extends Store {
  private PAST_PADDING: number = 90;
  private FUTURE_PADDING: number = 150;
  private SIMILAR_HOTELS_TO_SHOW: number = 10;

  constructor(
    private bexApiSource: BexApiSource,
    private hotelSearchApiSource: HotelSearchApiSource,
    private rateCalendarSource: RateCalendarApiSource,
    state: SerializedData = {},
    l10n: ILocalization,
    logger: Logger = NOOP_LOGGER
  ) {
    super(state, logger);

    this.l10n = l10n;

    this.searchCriteria = new SearchCriteriaModel();
    this.numberInterceptor('hotelId');
  }

  public hydrate(data: SerializedData): void {
    if (data && data.hotel && data.hotel.code === 'INACTIVE_HOTEL_ID') {
      window.location.replace(`https://${window.location.host}/Hotels`);
    }

    delete data.bexApiSource;
    delete data.shouldFetchInfo;
    delete data.hotelSearchApiSource;

    this.searchCriteria.hydrate(data.searchCriteria || {});
    delete data.searchCriteria;

    Object.assign(this, data);
  }

  // setup mobx interceptor to wrap dates in a method to format data passed to setter
  public numberInterceptor(prop): void {
    if (typeof prop === 'string') {
      intercept(this, prop as any, change => {
        if (change.newValue) {
          const newValue = this.sanitizeNumber(change.newValue);

          if (newValue) {
            change.newValue = newValue;

            return change;
          }
        }
      });
    }
  }

  public sanitizeNumber(number): number | string {
    try {
      const cleanNumber = typeof number === 'number' ? number : parseInt(number, 10);

      if (Number.isNaN(cleanNumber)) {
        throw new Error(`Invalid Number: cannot parse ${number} into a Number`);
      }

      return cleanNumber;
    } catch (error) {
      this.logErrorEvent(STORE_EVENTS.SANATIZE_NUMNBER_ERROR, getErrorString(error));

      return '';
    }
  }

  public static DEFAULTS: CurrentHotelStoreDefaults = {
    hotelId: null,
    selectedRoomType: '',
    selectedRatePlan: '',
    multiItemPriceToken: '',
    srpDisplayPrice: null,
    srpCurrency: '',
    l10n: new Localization('en_US'),
    offerSearchData: {},
    reviewIdsUpvoted: [],
    propertyRoomScoreInfo: [],
    propertyLocationScoreInfo: {},
    poiData: {},
    pageUsableMarked: false,
    selectedGalleryCategoryId: 'all',
    inRoomInfoDialog: false,
    shareInfo: null,
  };

  @observable
  public l10n: ILocalization;
  @observable
  public breadcrumbs: BreadcrumbsViewModel;
  @observable
  public toolbar: any;
  @observable
  public hotel: HotelDetailsViewModel;
  @observable
  public property: Partial<PropertyFields.Fragment>;
  @observable
  public map: HotelMapModel;
  @observable
  public offers: HotelRoomsAndRatesViewModel;
  @observable
  public reviewIdsUpvoted: string[];
  @observable
  public reviewScoreFilters: number[];
  @observable
  public reviewTravelerFilters: string[];
  @observable
  public reviewSortByFilter: string;
  @observable
  public reviewSearchTermFilters: string;
  @observable
  public propertyRoomScoreInfo: PropertyRoomScoreInfoModel[] = CurrentHotelStore.DEFAULTS.propertyRoomScoreInfo;
  @observable
  public propertyLocationScoreInfo: PropertyLocationScoreInfoModel = CurrentHotelStore.DEFAULTS.propertyLocationScoreInfo;
  @observable
  public shareInfo: ShareInfoModel = CurrentHotelStore.DEFAULTS.shareInfo;

  @observable
  public hotelId: number;
  @observable
  public selectedRoomType: string;
  @observable
  public selectedRatePlan: string;
  @observable
  public multiItemPriceToken: string;
  @observable
  public srpDisplayPrice: number;
  @observable
  public srpCurrency: string;
  @observable
  public referrer: string;
  @observable
  public offerTimestamp: string;
  @observable
  public isLoading: boolean;
  @observable
  public isFailure: boolean;
  @observable
  public shouldFetch: boolean;
  @observable
  public shouldFetchOffers: boolean;
  @observable
  public isHotelInfoCached: boolean;
  @observable
  public openNewDatePicker: boolean = false;
  @observable
  public loadingHotel: boolean;
  @observable
  public loadingOffers: boolean;
  @observable
  public defaultedDates: boolean;
  @observable
  public loadingETPData: boolean;
  @observable
  public creatingConversation: boolean;
  @observable
  public similarHotels: PropertySearchTravelAds.Properties[];
  @observable
  public similarHotelsDateless: HotelViewModel[];

  @observable
  public offerSearchData: any;

  @observable
  public travelAd: any;
  @observable
  public requests: any;
  @observable
  public shouldFetchLocationScore: boolean = false;
  @observable
  public freeLxMessagingCard: MessagingCardProps;

  @observable
  public rateCalendar: RateCalendarModel;
  @observable.ref
  public searchCriteria: SearchCriteriaModel;

  @observable
  public poiData: Record<string, unknown> = CurrentHotelStore.DEFAULTS.poiData;

  private _fetchingHotel: number;

  public selectedMapMarker: number;

  public infoExtensions: any;

  public offerExtensions: any;

  public defaultRequestFields: DefaultRequestFields;

  @observable
  public inRoomInfoDialog: boolean;

  // Temporary fields to hold the raw bex-api responses to be passed to the Offers shared-ui
  // component. Will be cleaned up once the shared-ui components query the fields themselves.
  @observable
  public sharedUiPropertyReviewAggregatesResponse: PropertyRoomScoreInfo.PropertyReviewAggregates | undefined;
  @observable
  public bexApiPropertyInfoOffersResponse: PropertyInfoOffersBexFeatures.PropertyInfo | undefined;
  @observable
  public sharedUiAmenitiesResponse: PropertyInfoDetails.Amenities | undefined;

  @observable
  public pageUsableMarked: boolean;
  private detailsFetched: boolean = false;

  @action
  public setPropertyListingAsCurrentHotel(property: PropertyFields.Fragment): void {
    if (!property) {
      return;
    }

    const { id, name, propertyGallery, shortList, star, starRatingIcon, vipMessaging } = property;

    const propertyId = parseInt(id, 10);

    this.toolbar = {
      name,
      stars: star,
    };

    if (this.hotelId === propertyId) {
      return;
    }

    this.shouldFetch = true;
    this.detailsFetched = false;
    this.pageUsableMarked = false;

    this.property = {
      id,
      name,
      propertyGallery: [propertyGallery?.[0]],
      shortList,
      star,
      starRatingIcon,
      vipMessaging,
    };

    this.hotelId = propertyId;
  }

  @action
  public updateSearchCriteria(criteria: SearchCriteriaModel): void {
    this.searchCriteria = criteria;
  }

  @action
  public clearDatelessContexts(context: ExtendedContextStore): void {
    if (this.hotel) {
      delete this.hotel.propertySummaryDescription;

      if (this.hotel.additionalInfo) {
        delete this.hotel.additionalInfo.propertyAlternativeName;
      }
    }

    if (context.footer) {
      delete context.footer;
    }
  }

  @action
  public setHotelId(hotelId) {
    if (hotelId !== this.hotelId) {
      this.shouldFetch = true;
      this.hotelId = hotelId;
    }
  }

  public getRoomScore(roomTypeCode: string): PropertyRoomScoreInfoModel | undefined {
    return this.propertyRoomScoreInfo?.find(x => x.id === roomTypeCode);
  }

  @action
  public resetFlyinMessage(): void {
    if (this.offers && this.offers.flyInModel) {
      this.offers.flyInModel.text = '';
      this.offers.flyInModel.icon = '';
    }
  }

  @action
  public createSessionBeforeMultiItemFetch(
    multiItem: MultiItemStore,
    context: ContextStore,
    incomingSearchCriteria: SearchCriteriaModel
  ): Promise<any> {
    return multiItem
      .createMultiItemSession(context, incomingSearchCriteria)
      .then(misId => {
        this.shouldFetch = true;
        incomingSearchCriteria.misId = misId;
      })
      .catch(() => {
        this.shouldFetch = false;
      });
  }

  @computed
  get canDisplayMultipleRatePlansInline(): boolean {
    if (this.searchCriteria.isDateless) {
      return false;
    }

    const propertyUnits = (this.bexApiPropertyInfoOffersResponse?.offers.listings?.filter(
      listing => listing.__typename === 'PropertyUnit'
    ) as unknown) as UnitFragment.Fragment[];
    const hasMultipleAvailableRatePlans = propertyUnits?.filter(propertyUnit => propertyUnit.ratePlans?.length > 1).length > 0;

    return hasMultipleAvailableRatePlans;
  }

  @computed
  get usable(): boolean {
    return this.detailsFetched;
  }

  @action
  public fetch(
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    multiItemStore: MultiItemStore,
    experiments: ExperimentStore,
    page: PageStore,
    additionalCriteria: any = {},
    isServer?: boolean
  ) {
    const { hotelId, searchCriteria } = this;

    const modelData = (searchCriteria && searchCriteria.toJSON()) || {};
    const criteriaJson = {
      ...InfositeQueryAdapter.parseOut(modelData, this.l10n),
      ...modelData,
    };
    this.logParallelCalls(hotelId); // for logging dups

    this.shouldFetch = false;
    this.isLoading = true;
    this.isFailure = false;

    const timeout = getOffersTimeout(multiItemStore.multiItemContext);

    const infoPromise = this.fetchInfo(context, timeout, criteriaJson, hotelId, multiItemStore.multiItemContext, page);
    const shouldDefer = this.shouldDoDeferringOperations(isServer, searchCriteria?.isDateless, context, experiments, page);
    const offerPromise = this.getOffersPromise(shouldDefer, context, timeout, additionalCriteria, multiItemStore);

    return Promise.all([infoPromise, offerPromise])
      .then(
        () =>
          new Promise(resolve => {
            runInAction('updating shared current hotel', () => {
              this.shouldFetch = false;
              this.isLoading = false;
              this.isFailure = false;
              this.shouldFetchOffers = shouldDefer;

              if (analyticsStore) {
                if (!this.loadingOffers) {
                  const infoOmnitureData = this.infoExtensions?.analytics[0];
                  const offersOmnitureData = this.offerExtensions?.analytics[0];
                  if (infoOmnitureData && offersOmnitureData) {
                    if (multiItemStore.isPackageShoppingPath()) {
                      analyticsStore.publishClientSidePayload([offersOmnitureData]);
                    } else {
                      const infoOmnitureCopy = JSON.parse(JSON.stringify(infoOmnitureData));
                      const offersOmnitureCopy = JSON.parse(JSON.stringify(offersOmnitureData));
                      mergeProductStrings(infoOmnitureCopy, offersOmnitureCopy);
                      delete offersOmnitureCopy.adobeMappedData.products;
                      const encodedReferrerUrl = this.searchCriteria.referrerUrl;
                      if (encodedReferrerUrl) {
                        infoOmnitureCopy.adobeMappedData.referrer = Base64.decode(encodedReferrerUrl);
                      }
                      analyticsStore.publishClientSidePayload([offersOmnitureCopy, infoOmnitureCopy]);
                    }
                  }
                }
              }
              resolve();
            });
          })
      )
      .catch(
        () =>
          new Promise(resolve => {
            runInAction('updating current hotel with failure', () => {
              this.shouldFetch = false;
              this.isFailure = true;
              this.isLoading = false;
              resolve();
            });
          })
      );
  }

  public shouldDoDeferringOperations(
    isServer: boolean,
    isDateless: boolean,
    context: ContextStore,
    experiments: ExperimentStore,
    page: PageStore
  ): boolean {
    let deferredOffersBucket = null;
    if (isServer && !isDateless) {
      deferredOffersBucket = evaluateAndLogExperiment('LS_Deferred_Offers_Fetch', context, experiments, page);
    }

    return deferredOffersBucket === 1;
  }

  public getOffersPromise(
    shouldDoDeferringOperations: boolean,
    context: ContextStore,
    timeout: number,
    additionalCriteria: any = {},
    multiItemStore?: MultiItemStore
  ): Promise<any> | undefined {
    return shouldDoDeferringOperations ? null : this.fetchOffers(context, timeout, additionalCriteria, multiItemStore);
  }

  private logParallelCalls(hotelId: number) {
    if (this.isLoading === true) {
      // overlapping calls in progress - logging to monitor if issue is widespread
      const message =
        'Overlapping calls in progress. CurrentHotelStore is fetching in parallel, ' +
        `firstHotelId=${this._fetchingHotel} secondHotelId=${hotelId}`;
      this.logger.logEvent(STORE_EVENTS.CURRENT_HOTEL_STORE_PARALLEL_FETCH, message);
    }
    this._fetchingHotel = hotelId;
  }

  private logErrorEvent(name, err) {
    this.logger.logEvent(name, getErrorString(err));
  }

  private shouldFetchInfo(hotelId, criteria): boolean {
    return (
      hotelId && // has valid hotelId for fetch
      (this.shouldFetch ||
      this.isFailure || // fetch is forced
      !this.hotel ||
      Boolean(this.hotel.defaultData) || // defaulted data
        (criteria && criteria.amenities && criteria.amenities.length > 0))
    );
  }

  @action
  public fetchInfo(
    context: ContextStore,
    timeout: number,
    criteria: any = {},
    hotelId: number,
    multiItemContext?: MultiItemContext,
    page?: PageStore
  ): Promise<any> {
    // since hotel info is static, this method is selective about fetching hotel info
    this.isHotelInfoCached = false;
    if (!this.shouldFetchInfo(hotelId, criteria)) {
      this.isHotelInfoCached = true;

      return Promise.resolve();
    }

    this.loadingHotel = true;

    if (this.hotel && hotelId !== this.hotel.id) {
      this.hotel = null;
    }

    const propertyInfoPromise = this.fetchPropertyInfoDetails(context, hotelId, page, multiItemContext, criteria).then(response => {
      const model = adaptPropertyInfoDetails(response);

      runInAction('update current hotel from response', () => {
        this.infoExtensions = response?.extensions;
        this.loadingHotel = false;
        this.hotel = model;
        this.toolbar = {
          name: this.hotel.hotelName,
        };

        // Save the amenities response temporarily for the shared-ui offers migration
        this.sharedUiAmenitiesResponse = response.data.propertyInfo.summary?.amenities;

        if (!criteria.regionId && model.region) {
          criteria.regionId = model.region.id;
          criteria.destination = model.region.name;
        }
      });

      this.detailsFetched = true;

      return model;
    });

    return timeoutPromise(propertyInfoPromise, timeout, 'fetchPropertyInfoDetails timeout error').catch(error => {
      runInAction('recovering from hotel info call failure', () => {
        this.loadingHotel = false;
        this.logErrorEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(error));
      });

      return { error };
    });
  }

  @action
  public fetchOffers(context: ContextStore, timeout: number, additionalData: any = {}, multiItemStore?: MultiItemStore): Promise<any> {
    // Note: always fetches offers -- does not check if a fetch is needed
    this.loadingOffers = true;
    this.defaultedDates = false;
    this.shouldFetchOffers = false;

    return this.fetchBexApiSchemaOffers(context, timeout, multiItemStore?.multiItemContext, additionalData)
      .then(data => {
        const start = Date.now();
        const model = adaptPropertyInfoOffers(data);

        runInAction('update current hotel offers from response', () => {
          this.loadingOffers = false;
          this.offers = null;
          this.offerExtensions = data?.extensions;
          if (!this.searchCriteria.useRewards) {
            this.searchCriteria.useRewards = this.getUseRewards(data);
          }

          // Store the raw offers response temporarily for the shared-ui offers migration
          this.bexApiPropertyInfoOffersResponse = {
            offers: {
              listingsHeader: data.data.propertyInfo.offers?.listingsHeader,
              errorMessage: data.data.propertyInfo.offers?.errorMessage,
              experimental: data.data.propertyInfo.offers?.experimental,
              listings: data.data.propertyInfo.offers?.listings,
              tripSummary: data.data.propertyInfo.offers?.tripSummary,
              paymentPolicy: data.data.propertyInfo.offers?.paymentPolicy,
              spaceDetails: data.data.propertyInfo.offers?.spaceDetails,
              singleUnitOffer: data.data.propertyInfo.offers?.singleUnitOffer,
              singleUnitOfferDialog: data.data.propertyInfo.offers?.singleUnitOfferDialog,
              propertyLevelOffersMessage: data.data.propertyInfo.offers?.propertyLevelOffersMessage,
              offerLevelMessages: data.data.propertyInfo.offers?.offerLevelMessages,
              stickyBar: data.data.propertyInfo.offers?.stickyBar,
              alternateDates: data.data.propertyInfo.offers?.alternateDates,
              soldOut: data.data.propertyInfo.offers?.soldOut,
              shoppingContext: data.data.propertyInfo.offers?.shoppingContext,
            },
          };

          const bexApiOmnitureAbsent =
            !this.offerExtensions || !this.offerExtensions.analytics || this.offerExtensions.analytics.size === 0;
          if (bexApiOmnitureAbsent && this.logger && typeof this.logger.logEvent === 'function') {
            const error = new Error('Empty omnitureData returned with offers');
            this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(error));
          }

          this.logger.logEvent(STORE_EVENTS.BEX_API_OFFERS_ADAPT_TIME, `${(Date.now() - start).toString()} ms`);
          this.offers = model;
          /* istanbul ignore next */
          if (this.offers.shoppingContext?.multiItem) {
            const { id } = this.offers.shoppingContext.multiItem;
            multiItemStore.updateSessionId(id);
            this.searchCriteria.misId = id;
            this.defaultRequestFields = { misId: id };
          }
        });

        this.defaultedDates = this.getDefaultedDates(data);

        return model;
      })
      .catch(error => {
        runInAction('recovering from hotel offers call breakdown', () => {
          this.loadingOffers = false;
          this.isFailure = true;
          this.logErrorEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(error));
        });

        return { error };
      });
  }

  private getUseRewards(data) {
    const rewardsData = data?.data?.propertyInfo?.offers?.searchCriteria?.secondary?.selections?.find(
      selection => selection.value === Points.SHOP_WITH_POINTS
    );

    return rewardsData ? 'true' : 'false';
  }

  private getDefaultedDates(data) {
    return (
      !this.searchCriteria.startDate &&
      !this.searchCriteria.endDate &&
      data.data.propertyInfo.offers?.searchCriteria?.primary.dateRange !== null
    );
  }

  private fetchPropertyInfoDetails(
    context: ContextStore,
    propertyId: number,
    page?: PageStore,
    multiItemContext?: MultiItemContext,
    criteria?: SearchCriteriaModel
  ) {
    const variables: PropertyInfoDetails.Variables = getPropertyInfoInput(context, propertyId.toString(), multiItemContext, criteria);

    // eslint-disable-next-line dot-notation
    return this.bexApiSource.fetchPropertyInfoDetails(variables, page?.pageId, context['traceInfo']['Trace-ID']);
  }

  private fetchBexApiSchemaOffers(
    context: ContextStore,
    timeout: number,
    multiItemContext?: MultiItemContext,
    additionalCriteria?: Record<string, unknown>
  ) {
    const variables: PropertyInfoOffersBexFeatures.Variables = getPropertyOffersInput(context, this, multiItemContext, additionalCriteria);

    return this.bexApiSource.fetchPropertyInfoOffersBexFeatures(variables, timeout);
  }

  @action
  public async fetchPropertyAvailabilityCheck(
    context: ContextStore,
    formData: any
  ): Promise<OperationResult<PropertyAvailabilityCheck.Query>> {
    const { hotelId, searchCriteria } = this;
    const criteria = Object.assign({}, searchCriteria, InfositeQueryAdapter.parseIn(formData));
    this.loadingOffers = true;
    const variables: PropertyAvailabilityCheck.Variables = getPropertyAvailabilityCheck(context, criteria, hotelId.toString());

    return new Promise<OperationResult<PropertyAvailabilityCheck.Query>>(async resolve => {
      const data = await this.bexApiSource.fetchPropertyAvailabilityCheck(variables);
      this.loadingOffers = false;
      resolve(data);
    });
  }

  @action
  public setOfferData(offerData): void {
    this.offerSearchData = Object.assign({}, this.offerSearchData, offerData);
  }

  @action
  public setBreadcrumbsModel(regionList: any[]): void {
    let breadcrumbsModule;
    const breadcrumbsList = [];

    if (regionList.length >= 1) {
      breadcrumbsModule = regionList[0].modules.find(module => module.name === 'breadcrumbs');
    }

    if (
      breadcrumbsModule &&
      breadcrumbsModule.model &&
      breadcrumbsModule.model.breadcrumbs &&
      breadcrumbsModule.model.breadcrumbs.length > 0
    ) {
      breadcrumbsModule.model.breadcrumbs.forEach(crumb => {
        breadcrumbsList.push({ text: crumb.text, url: crumb.url, fullUrl: crumb.fullUrl, index: crumb.index });
      });
    }

    const newBreadcrumb = {
      crumbs: breadcrumbsList,
    };

    this.breadcrumbs = newBreadcrumb;
  }

  @action
  public clearRoomsFromOffers(): void {
    return this.offers && this.offers.rooms && (this.offers.rooms = null);
  }

  @action
  public clearRecommendedOfferFromOffers(): void {
    return this.offers && this.offers.recommendedOffer && (this.offers.recommendedOffer = null);
  }

  @action
  public fetchPropertyLocationScoreInfo(context: ContextStore, propertyId: number, pageId: string): Promise<any> {
    const contextInput: ContextInput = getBexApiContext(context);
    const variables: PropertyLocationScoreInfo.Variables = {
      context: contextInput,
      propertyIds: [propertyId.toString()],
    };

    return this.bexApiSource
      .fetchPropertyLocationScoreInfo(variables, pageId, getDefaultTimeout())
      .then(({ data }) => {
        runInAction('update propertyLocationScoreInfo from response', () => {
          if (
            data.propertyReviewAggregates &&
            data.propertyReviewAggregates.propertySummaries &&
            data.propertyReviewAggregates.propertySummaries.length
          ) {
            this.propertyLocationScoreInfo = data.propertyReviewAggregates.propertySummaries[0].locationScore;
          }
        });
      })
      .catch(error => {
        runInAction('recovering from bex api property info location score call breakdown', () => {
          this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, variables, getErrorString(error));
        });

        return {
          error,
        };
      });
  }

  @action
  public fetchShareInfo(context: ContextStore, hotelId: number, pageId: string, multiItemContext: MultiItemContext): Promise<any> {
    const contextInput: ContextInput = getBexApiContext(context);

    const variables: ShareInfo.Variables = {
      context: contextInput,
      propertyId: hotelId.toString(),
      shoppingContext: getShoppingContextInput(multiItemContext),
    };

    return this.bexApiSource
      .fetchShareInfo(variables, pageId, getDefaultTimeout())
      .then(({ data }) => {
        if (data.propertyInfo && data.propertyInfo.shareInfo) {
          this.shareInfo = data.propertyInfo.shareInfo;
        }
      })
      .catch(error => {
        runInAction('recovering from bex api share info call breakdown', () => {
          this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, variables, getErrorString(error));
        });

        return {
          error,
        };
      });
  }

  @action
  public fetchPropertyRoomScoreInfo(context: ContextStore, propertyId: number, pageId: string): Promise<any> {
    const contextInput: ContextInput = getBexApiContext(context);
    const variables: PropertyRoomScoreInfo.Variables = {
      context: contextInput,
      propertyIds: [propertyId.toString()],
    };
    this.propertyRoomScoreInfo = null;

    return this.bexApiSource
      .fetchPropertyRoomScoreInfo(variables, pageId, getDefaultTimeout())
      .then(({ data }) => {
        if (
          data.propertyReviewAggregates &&
          data.propertyReviewAggregates.propertySummaries &&
          data.propertyReviewAggregates.propertySummaries.length
        ) {
          this.propertyRoomScoreInfo = data.propertyReviewAggregates.propertySummaries[0].roomsScore;
          this.sharedUiPropertyReviewAggregatesResponse = data.propertyReviewAggregates;
        }
      })
      .catch(error => {
        runInAction('recovering from bex api property info room score call breakdown', () => {
          this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, variables, getErrorString(error));
        });

        return {
          error,
        };
      });
  }

  @action
  public fetchHISRates(l10n, requestStartDate, requestEndDate, adults, children, hotelId, context) {
    const { startDate, endDate } = this.resolveSearchDates(l10n, requestStartDate, requestEndDate);

    const searchCriteria: { startDate: string; endDate: string; adults: number; children: number; calendarType?: string } = {
      startDate,
      endDate,
      adults,
      children,
    };
    if (this.searchCriteria.isDateless) {
      searchCriteria.calendarType = 'highlow';
    }

    return this.rateCalendarSource
      .fetchHIS(context, searchCriteria, hotelId)
      .then(res => {
        if (res && res.datePickerContent) {
          this.rateCalendar = res;
        } else {
          this.rateCalendar = {};
        }

        return this.rateCalendar;
      })
      .catch(error => {
        runInAction('rate calendar call failure', () => {
          this.logErrorEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(error));
        });
      });
  }

  @action
  public getUpdatedBackToHsrUrl(formData) {
    const { startDate, endDate, adults, children } = formData;
    const relativePath = this.offers?.propertySearchLink?.link.uri.relativePath;
    const [hsrPathName, hsrQuery] = relativePath.split('?');
    const updatedQuery = Object.assign({}, parse(hsrQuery), {
      selected: this.hotelId,
      startDate,
      endDate,
      adults,
      children,
    });

    return `${hsrPathName}?${stringify(updatedQuery)}`;
  }

  @computed
  get hsrQueryValues() {
    const relativePath = this.offers?.propertySearchLink?.link.uri.relativePath;
    if (relativePath) {
      const hsrQuery = relativePath.split('?')[1];
      const searchQuery = parse(hsrQuery);
      searchQuery.selected = this.hotelId;

      delete searchQuery.adults;
      delete searchQuery.children;
      delete searchQuery.startDate;
      delete searchQuery.endDate;

      return searchQuery;
    }

    return {};
  }

  private resolveSearchDates(l10n, requestStartDate, requestEndDate) {
    const dateUtils = new DateUtils(l10n);

    const today = dateUtils.getToday();
    const endDate = dateUtils.getNDaysFrom(requestEndDate, this.FUTURE_PADDING);
    const daysBetweenTodayAndRequestStartDate = dateUtils.getDaysBetween(today, requestStartDate);
    let startDate;
    if (daysBetweenTodayAndRequestStartDate <= this.PAST_PADDING && daysBetweenTodayAndRequestStartDate >= 0) {
      startDate = today;
    } else {
      startDate = dateUtils.getNDaysFrom(requestStartDate, -this.PAST_PADDING);
    }

    return { startDate, endDate };
  }

  /**
   * Fetches sponsored hotels (MeSo) shown on the dateless pdp page.
   * These may or may not be very different from the actual current hotel.
   * @param context
   * @param experiments
   * @param multiItemContext
   */
  @action
  public fetchSimilarHotels(context: ContextStore, multiItemContext: MultiItemContext, pageId: string) {
    this.similarHotels = null;

    if (!this.hotelId || !this.searchCriteria || !this.searchCriteria.regionId) {
      return Promise.resolve();
    }

    const criteriaWithParsedGuests = SearchQueryAdapter.parseOut(this.searchCriteria && this.searchCriteria.toJSON());

    const similarHotelsCriteria = {
      ...criteriaWithParsedGuests,
      count: this.SIMILAR_HOTELS_TO_SHOW,
    };

    if (!this.searchCriteria.star) {
      similarHotelsCriteria.star = '10,20,30,40,50';
    }

    const promise: Promise<any> = this.fetchTravelAdPropertySearch(context, similarHotelsCriteria, multiItemContext, pageId);

    return promise
      .then(response => {
        runInAction('updating dated HIS travelAd carousel results', () => {
          if (response.data) {
            this.similarHotels = getPropertiesFromResults(response.data);
          } else {
            this.similarHotels = response;
          }
        });
      })
      .catch(error => {
        runInAction('dated HIS carousel similar hotels call failure', () => {
          this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(error));
        });
      });
  }

  private fetchTravelAdPropertySearch(
    context: ContextStore,
    criteria: SearchCriteriaModel,
    multiItemContext: MultiItemContext,
    pageId: string
  ) {
    const variables: LodgingPwaPropertySearch.Variables = getPropertySearchInput(context, criteria, null, multiItemContext, true);
    variables.destination.pinnedPropertyId = this.hotelId.toString();

    return this.bexApiSource.fetchTravelAdPropertySearch(variables, pageId);
  }

  /**
   * Fetches similar hotels
   * @param context
   * @param flexViewModelResponse
   */
  @action
  public fetchSimilarHotelsDateless(context: ContextStore, timeout: number, flexViewModelResponse: ViewModelResponse) {
    if (
      !flexViewModelResponse ||
      !flexViewModelResponse.flexContext ||
      !flexViewModelResponse.flexContext.searchContext ||
      !flexViewModelResponse.flexContext.searchContext.location
    ) {
      return Promise.resolve();
    }
    const location: Geo = flexViewModelResponse.flexContext.searchContext.location;
    const hotelId = location.id;
    const regionId = location.parentId;
    const similarHotelsCriteria = {
      regionId,
      selected: hotelId,
      pinnedHotelId: hotelId,
      hadPinnedHotel: true,
      count: 5, // while swagger does show this, it doesn't seem to do anything...
      sort: PropertySort.RECOMMENDED,
      adults: 2,
    };

    return this.hotelSearchApiSource
      .fetch(context, timeout, similarHotelsCriteria)
      .then(
        data =>
          new Promise(resolve => {
            runInAction('update current hotel similarHotelsDateless', () => {
              if (data && data.hotels) {
                this.similarHotelsDateless = data.hotels
                  .filter(entry => entry.resultType === 'HOTEL')
                  .filter(hotel => hotel.hotelId !== hotelId)
                  .slice(0, 10);
              }
              resolve();
            });
          })
      )
      .catch(err => {
        this.logger.logEvent(STORE_EVENTS.FETCH_ERROR, getErrorString(err));

        return Promise.resolve();
      });
  }

  @action
  public clearPoiData() {
    this.poiData = CurrentHotelStore.DEFAULTS.poiData;
  }

  @action
  public setOpenNewDatePicker(value: boolean) {
    this.openNewDatePicker = value;
  }

  public setSelectedMapMarker(markerId: number) {
    this.selectedMapMarker = markerId ? Number(markerId) : markerId;
  }
}
